import { Check } from 'lucide-react';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { classOptions } from 'src/app/Orders/order.const';
import { filterServicesByType } from 'src/app/Orders/order.helpers';
import BadgeCmp from 'src/components/BadgeCmp';
import CardCmp from 'src/components/CardCmp';
import TruncateTextCmp from 'src/components/TruncateText';
import { orderDetailsData, orderDetailsLoading, selectCurrentLegData, selectHasLegTabs, selectOrderLegActiveTab } from 'src/redux/orders.slice';
import { RootState } from 'src/redux/store';
import { getFormattedAmount } from 'src/utils/CommonFunctions';

const ServiceItem = ({ service, loading }: { service: any; loading: boolean }) => {
    const serviceRef = useRef<HTMLParagraphElement>(null);

    return (
        <div className="w-[calc(50%_-_4px)] flex items-center gap-x-1.5">
            <Check className="w-3.5 h-3.5 text-primary flex-none" />
            <TruncateTextCmp message={loading ? '' : service?.name || '-'} refName={serviceRef} truncateTextParentClassName="w-full">
                <p className="text-textSecondary text-xs leading-[18px] font-normal truncate flex-1" ref={serviceRef}>
                    {service?.name || '-'}
                </p>
            </TruncateTextCmp>
        </div>
    );
};

const OrderDetailsSection = () => {
    const { t } = useTranslation();
    const loading = useSelector(orderDetailsLoading);
    const orderDetails = useSelector(orderDetailsData);
    const currentLegData = useSelector(selectCurrentLegData);
    const orderLegActiveTab = useSelector(selectOrderLegActiveTab);
    const hasLegTabs = useSelector(selectHasLegTabs);
    const services = useSelector((state: RootState) => state.basic.services);

    // Master tab = order-level rollup; leg tab = that leg's own fields. Leg-only fields
    // (pickup/delivery number, quote) have no order-level equivalent and show '-' on master.
    //
    // A single-presented-leg order is the exception: its master tab IS that one leg, there is no
    // leg tab to switch to, and currentLegData already points at it — so read the leg's fields and
    // the leg-only ones stop showing '-' (client, 2026-10-09).
    const isMaster = !!orderLegActiveTab?.isMaster && hasLegTabs;
    const serviceIds = isMaster ? Array.from(new Set((orderDetails?.orderLegs ?? []).flatMap((leg: any) => leg?.additionalServices?.serviceId ?? []))) : currentLegData?.additionalServices?.serviceId;
    const equipmentTypeFullName = isMaster ? orderDetails?.orderLegs?.[0]?.equipmentTypeFullName : currentLegData?.equipmentTypeFullName;
    const linearFootage = isMaster ? orderDetails?.linearFootage : currentLegData?.linearFootage;
    const totalWeight = isMaster ? orderDetails?.totalWeight : currentLegData?.totalWeight;
    const refNumber = isMaster ? orderDetails?.refNumber : currentLegData?.refNumber;
    const poNumber = isMaster ? orderDetails?.poNumber : currentLegData?.poNumber;
    const loadInsuranceId = isMaster ? orderDetails?.loadInsuranceId : currentLegData?.loadInsuranceId;
    const pickupNumber = isMaster ? null : currentLegData?.pickupNumber;
    const deliveryNumber = isMaster ? null : currentLegData?.deliveryNumber;
    const quoteCode = isMaster ? null : currentLegData?.quote?.code;

    // Commodity + commercial (declared) value are ORDER-level — one value for the whole journey
    // (wal-backend-v2 migration 20260928130549). Same value on the master tab and on every leg
    // tab; the legacy per-leg order_leg_metadata copies are deliberately not read here.
    const commodity = orderDetails?.commodity
        ? orderDetails.commodity
              .split('|')
              .filter((item: string) => item.trim() !== '')
              .join(' | ')
        : '';
    const commercialValue = orderDetails?.declaredValue ? `$${getFormattedAmount(orderDetails.declaredValue)} ${orderDetails?.declaredValueUnit || ''}`.trim() : '';

    const pickupAdditionalService = filterServicesByType(services, serviceIds, 'PICKUP');
    const deliveryAdditionalService = filterServicesByType(services, serviceIds, 'DELIVERY');
    const otherAdditionalService = filterServicesByType(services, serviceIds, 'OTHER');

    const weightUnit = (isMaster ? orderDetails?.order_dimensions : currentLegData?.order_dimensions)?.[0]?.weightMeasurement === 'KGS' ? 'kgs' : 'lbs';

    const equipmentTypeRef = useRef<HTMLParagraphElement>(null);
    const linearFootageRef = useRef<HTMLParagraphElement>(null);
    const totalWeightRef = useRef<HTMLParagraphElement>(null);
    const refNumberRef = useRef<HTMLParagraphElement>(null);
    const commodityRef = useRef<HTMLParagraphElement>(null);
    const commercialValueRef = useRef<HTMLParagraphElement>(null);
    const poNumberRef = useRef<HTMLParagraphElement>(null);
    const loadInsuranceIdRef = useRef<HTMLParagraphElement>(null);
    const pickupNumberRef = useRef<HTMLParagraphElement>(null);
    const deliveryNumberRef = useRef<HTMLParagraphElement>(null);
    const quoteIdRef = useRef<HTMLParagraphElement>(null);

    const hasServices =
        (pickupAdditionalService && pickupAdditionalService.length > 0) ||
        (deliveryAdditionalService && deliveryAdditionalService.length > 0) ||
        (otherAdditionalService && otherAdditionalService.length > 0) ||
        loading;

    return (
        <CardCmp headerTitle={t('Orders.Order_Details')} headerParentClassName="!rounded-t-lg !bg-transparent" isAccordion={hasServices} specificSectionAccordion={hasServices}>
            <div className="py-3 flex flex-wrap gap-3">
                <div className="w-[calc(50%_-_10px)] lg:w-[calc(20%_-_10px)]">
                    <p className="text-gray500 text-xs leading-[18px] font-normal">{t('Orders.Order_Type')}</p>
                    {loading ? (
                        <p className="text-grayLight900 text-sm font-medium custom-loading">lorem ipsum</p>
                    ) : orderDetails?.orderType ? (
                        <BadgeCmp style="modern" type="success" mainClassName="bg-white">
                            {classOptions?.find((option: any) => option?.value === orderDetails?.orderType)?.label || t('Common.LTL')}
                        </BadgeCmp>
                    ) : (
                        <p className="text-grayLight900 text-sm font-medium">-</p>
                    )}
                </div>

                <div className="w-[calc(50%_-_10px)] lg:w-[calc(20%_-_10px)] min-w-0">
                    <p className="text-gray500 text-xs leading-[18px] font-normal">{t('Common.Equipment_Type')}</p>
                    <TruncateTextCmp message={loading ? '' : equipmentTypeFullName || '-'} refName={equipmentTypeRef}>
                        <p className={`text-grayLight900 text-sm font-medium truncate ${loading ? 'custom-loading' : ''}`} ref={equipmentTypeRef}>
                            {loading ? 'lorem ipsum' : equipmentTypeFullName || '-'}
                        </p>
                    </TruncateTextCmp>
                </div>

                <div className="w-[calc(50%_-_10px)] lg:w-[calc(20%_-_10px)] min-w-0">
                    <p className="text-gray500 text-xs leading-[18px] font-normal">{t('Common.Linear_Footage')}</p>
                    <TruncateTextCmp message={loading ? '' : linearFootage ? `${linearFootage} Ft.` : '-'} refName={linearFootageRef}>
                        <p className={`text-grayLight900 text-sm font-medium truncate ${loading ? 'custom-loading' : ''}`} ref={linearFootageRef}>
                            {loading ? 'lorem ipsum' : linearFootage ? `${linearFootage} Ft.` : '-'}
                        </p>
                    </TruncateTextCmp>
                </div>

                <div className="w-[calc(50%_-_10px)] lg:w-[calc(20%_-_10px)] min-w-0">
                    <p className="text-gray500 text-xs leading-[18px] font-normal">{t('Common.Total_Weight')}</p>
                    <TruncateTextCmp message={loading ? '' : totalWeight ? `${totalWeight} ${weightUnit}` : '-'} refName={totalWeightRef}>
                        <p className={`text-grayLight900 text-sm font-medium block truncate ${loading ? 'custom-loading' : ''}`} ref={totalWeightRef}>
                            {loading ? 'lorem ipsum' : totalWeight ? `${totalWeight} ${weightUnit}` : '-'}
                        </p>
                    </TruncateTextCmp>
                </div>

                <div className="w-[calc(50%_-_10px)] lg:w-[calc(20%_-_10px)] min-w-0">
                    <p className="text-gray500 text-xs leading-[18px] font-normal">{t('Orders.Reference_Number')}</p>
                    <TruncateTextCmp message={loading ? '' : refNumber || '-'} refName={refNumberRef}>
                        <p className={`text-grayLight900 text-sm font-medium truncate ${loading ? 'custom-loading' : ''}`} ref={refNumberRef}>
                            {loading ? 'lorem ipsum' : refNumber || '-'}
                        </p>
                    </TruncateTextCmp>
                </div>

                <div className="w-[calc(50%_-_10px)] lg:w-[calc(20%_-_10px)] min-w-0">
                    <p className="text-gray500 text-xs leading-[18px] font-normal">{t('Common.Commodity')}</p>
                    <TruncateTextCmp message={loading ? '' : commodity || '-'} refName={commodityRef}>
                        <p className={`text-grayLight900 text-sm font-medium truncate ${loading ? 'custom-loading' : ''}`} ref={commodityRef}>
                            {loading ? 'lorem ipsum' : commodity || '-'}
                        </p>
                    </TruncateTextCmp>
                </div>

                <div className="w-[calc(50%_-_10px)] lg:w-[calc(20%_-_10px)] min-w-0">
                    <p className="text-gray500 text-xs leading-[18px] font-normal">{t('Common.Commercial_Value')}</p>
                    <TruncateTextCmp message={loading ? '' : commercialValue || '-'} refName={commercialValueRef}>
                        <p className={`text-grayLight900 text-sm font-medium truncate ${loading ? 'custom-loading' : ''}`} ref={commercialValueRef}>
                            {loading ? 'lorem ipsum' : commercialValue || '-'}
                        </p>
                    </TruncateTextCmp>
                </div>

                <div className="w-[calc(50%_-_10px)] lg:w-[calc(20%_-_10px)]">
                    <p className="text-gray500 text-xs leading-[18px] font-normal">{t('Orders.PO_Number')}</p>
                    <TruncateTextCmp message={loading ? '' : poNumber || '-'} refName={poNumberRef}>
                        <p className={`text-grayLight900 text-sm font-medium truncate ${loading ? 'custom-loading' : ''}`} ref={poNumberRef}>
                            {loading ? 'lorem ipsum' : poNumber || '-'}
                        </p>
                    </TruncateTextCmp>
                </div>

                <div className="w-[calc(50%_-_10px)] lg:w-[calc(20%_-_10px)]">
                    <p className="text-gray500 text-xs leading-[18px] font-normal">{t('Orders.Load_Insurance_ID')}</p>
                    <TruncateTextCmp message={loading ? '' : loadInsuranceId ? `#${loadInsuranceId}` : '-'} refName={loadInsuranceIdRef}>
                        <p className={`text-grayLight900 text-sm font-medium truncate ${loading ? 'custom-loading' : ''}`} ref={loadInsuranceIdRef}>
                            {loading ? 'lorem ipsum' : loadInsuranceId ? `#${loadInsuranceId}` : '-'}
                        </p>
                    </TruncateTextCmp>
                </div>

                <div className="w-[calc(50%_-_10px)] lg:w-[calc(20%_-_10px)]">
                    <p className="text-gray500 text-xs leading-[18px] font-normal">{t('Orders.Pickup_Number')}</p>
                    <TruncateTextCmp message={loading ? '' : pickupNumber || '-'} refName={pickupNumberRef}>
                        <p className={`text-grayLight900 text-sm font-medium truncate ${loading ? 'custom-loading' : ''}`} ref={pickupNumberRef}>
                            {loading ? 'lorem ipsum' : pickupNumber || '-'}
                        </p>
                    </TruncateTextCmp>
                </div>

                <div className="w-[calc(50%_-_10px)] lg:w-[calc(20%_-_10px)]">
                    <p className="text-gray500 text-xs leading-[18px] font-normal">{t('Orders.Delivery_Number')}</p>
                    <TruncateTextCmp message={loading ? '' : deliveryNumber || '-'} refName={deliveryNumberRef}>
                        <p className={`text-grayLight900 text-sm font-medium truncate ${loading ? 'custom-loading' : ''}`} ref={deliveryNumberRef}>
                            {loading ? 'lorem ipsum' : deliveryNumber || '-'}
                        </p>
                    </TruncateTextCmp>
                </div>

                <div className="w-[calc(50%_-_10px)] lg:w-[calc(20%_-_10px)]">
                    <p className="text-gray500 text-xs leading-[18px] font-normal">{t('QuotingHub.QuotingDashboard.Quote_ID')}</p>
                    <TruncateTextCmp message={loading ? '' : quoteCode ? `#${quoteCode}` : '-'} refName={quoteIdRef}>
                        <p className={`text-grayLight900 text-sm font-medium truncate ${loading ? 'custom-loading' : ''}`} ref={quoteIdRef}>
                            {loading ? 'lorem ipsum' : quoteCode ? `#${quoteCode}` : '-'}
                        </p>
                    </TruncateTextCmp>
                </div>
            </div>

            {hasServices && (
                <div className="thisSectionAccordion">
                    <div className="overflow-hidden">
                        <div className="border-t border-utilityGray200 py-3">
                            {loading ? (
                                <div className="flex items-stretch divide-x divide-utilityGray200">
                                    {Array.from({ length: 3 }, (_, col) => (
                                        <div key={col} className={`w-full lg:w-[calc(33.33%_-_8px)] flex flex-col ${col === 0 ? 'pr-4' : col === 1 ? 'px-4' : 'pl-4'}`}>
                                            <div className="flex items-center gap-2 mb-3">
                                                <p className="text-grayLight900 text-xs leading-[1.5] font-semibold custom-loading w-40">lorem ipsum dolor sit</p>
                                                <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded custom-loading">0</span>
                                            </div>
                                            <div className="space-y-2">
                                                {Array.from({ length: 4 }, (_r, row) => (
                                                    <div key={row} className="flex items-center gap-x-1.5">
                                                        <div className="w-3.5 h-3.5 custom-loading flex-none rounded-full" />
                                                        <p className="text-grayLight600 text-xs leading-[18px] font-normal custom-loading w-full">lorem ipsum dolor sit amet</p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="flex items-stretch divide-x divide-utilityGray200">
                                    {/* Pickup Additional Services */}
                                    <div className="w-full lg:w-1/3 flex flex-col pr-4">
                                        <div className="flex items-center gap-2">
                                            <p className="text-grayLight900 text-xs leading-[18px] font-medium">{t('Orders.Pickup_Additional_Services')}</p>
                                            {pickupAdditionalService && pickupAdditionalService.length > 0 && (
                                                <BadgeCmp style="modern" type="success" mainClassName="bg-white" isHidePillDot>
                                                    {pickupAdditionalService.length}
                                                </BadgeCmp>
                                            )}
                                        </div>
                                        <div className="mt-2 p-3 rounded-lg bg-gray50 shadow-sm flex gap-2 flex-wrap items-start content-start flex-1 overflow-y-auto custom-scrollbar-v2 h-full">
                                            {pickupAdditionalService && pickupAdditionalService.length > 0 ? (
                                                pickupAdditionalService.map((service: any, index: number) => <ServiceItem key={`pickup-${index}`} service={service} loading={loading} />)
                                            ) : (
                                                <div className="w-full m-auto">
                                                    <p className="text-grayLight600 text-xs leading-[18px] font-medium text-center">{t('Common.None')}</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Delivery Additional Services */}
                                    <div className="w-full lg:w-1/3 flex flex-col px-4">
                                        <div className="flex items-center gap-2">
                                            <p className="text-grayLight900 text-xs leading-[18px] font-medium">{t('Orders.Delivery_Additional_Services')}</p>
                                            {deliveryAdditionalService && deliveryAdditionalService.length > 0 && (
                                                <BadgeCmp style="modern" type="success" mainClassName="bg-white" isHidePillDot>
                                                    {deliveryAdditionalService.length}
                                                </BadgeCmp>
                                            )}
                                        </div>
                                        <div className="mt-2 p-3 rounded-lg bg-gray50 shadow-sm flex gap-2 flex-wrap items-start content-start flex-1 overflow-y-auto custom-scrollbar-v2 h-full">
                                            {deliveryAdditionalService && deliveryAdditionalService.length > 0 ? (
                                                deliveryAdditionalService.map((service: any, index: number) => <ServiceItem key={`delivery-${index}`} service={service} loading={loading} />)
                                            ) : (
                                                <div className="w-full m-auto">
                                                    <p className="text-grayLight600 text-xs leading-[18px] font-medium text-center">{t('Common.None')}</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Other Additional Services */}
                                    <div className="w-full lg:w-1/3 flex flex-col pl-4">
                                        <div className="flex items-center gap-2">
                                            <p className="text-grayLight900 text-xs leading-[18px] font-medium">{t('Common.Other_Additional_Services')}</p>
                                            {otherAdditionalService && otherAdditionalService.length > 0 && (
                                                <BadgeCmp style="modern" type="success" mainClassName="bg-white" isHidePillDot>
                                                    {otherAdditionalService.length}
                                                </BadgeCmp>
                                            )}
                                        </div>
                                        <div className="mt-2 p-3 rounded-lg bg-gray50 shadow-sm flex gap-2 flex-wrap items-start content-start flex-1 overflow-y-auto custom-scrollbar-v2 h-full">
                                            {otherAdditionalService && otherAdditionalService.length > 0 ? (
                                                otherAdditionalService.map((service: any, index: number) => <ServiceItem key={`other-${index}`} service={service} loading={loading} />)
                                            ) : (
                                                <div className="w-full m-auto">
                                                    <p className="text-grayLight600 text-xs leading-[18px] font-medium text-center">{t('Common.None')}</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </CardCmp>
    );
};

export default OrderDetailsSection;
